<template lang="html">
  <div class="sign-in-mfa-setup-view view">
    <router-link class="back-button" :to="{ name: signInRouteName }">
      <v-icon>arrow_back</v-icon>{{ $t('back') }}
    </router-link>

    <h1>{{ $t('setupMFA.title') }}</h1>
    <div class="description">
      {{ $t('setupMFA.description') }}
    </div>

    <div class="steps-wrapper">
      <div class="step step1">{{ $t('setupMFA.step1') }}</div>

      <div class="step step2">{{ $t('setupMFA.step2') }}</div>
      <qrcode v-if="qrcodeStr" :value="qrcodeStr" :options="{ width: 160, margin: 2 }" class="qrcode"></qrcode>

      <div class="step step3">{{ $t('setupMFA.step3') }}</div>
      <verify-mfa-code-form :submited="submited" @submit="submit" :error="errorMessage" />
    </div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VueQrcode from '@chenfengyuan/vue-qrcode';

import { getSignInNextStepRoute, challengeSetupMFA, challengeVerifySetupMFACode } from '@/amplify/auth';
import { authErrors } from '@/amplify/errors';
import { views, DEFAULT_LOGGED_IN_ROUTE } from '@/router/constants';

import VerifyMFACodeForm from '@/components/forms/VerifyMFACodeForm.vue';

export default {
  name: 'SignInMFASetup',
  components: {
    'verify-mfa-code-form': VerifyMFACodeForm,
    qrcode: VueQrcode,
  },
  data() {
    return {
      errorMessage: '',
      submited: false,
      qrcodeStr: '',
    };
  },
  computed: {
    ...mapGetters(['authChallenge']),
    signInRouteName: () => views.SIGN_IN,
  },
  async mounted() {
    try {
      const nestAuthStepRouteName = getSignInNextStepRoute();
      if (nestAuthStepRouteName && nestAuthStepRouteName !== views.SIGN_IN_MFA_SETUP) {
        await this.$router.push({ name: nestAuthStepRouteName });
        return;
      }

      this.qrcodeStr = await challengeSetupMFA();
    } catch (e) {
      this.errorMessage = this.$t('error.general');
    }
  },
  methods: {
    async submit({ otpCode }) {
      this.submited = true;

      try {
        await challengeVerifySetupMFACode(otpCode);
        await this.$router.push({ name: DEFAULT_LOGGED_IN_ROUTE });
        this.submited = false;
      } catch (e) {
        this.submited = false;
        const { code, message } = e || {};

        if (code === authErrors.NOT_AUTHORIZED) {
          this.errorMessage = message;
        } else if (code === authErrors.SETUP_MFA) {
          this.errorMessage = this.$t('error.incorrectVerificationCode');
        } else if (code === authErrors.CHALLENGE_REQUIRED) {
          const nextRouteName = getSignInNextStepRoute();

          if (nextRouteName) {
            this.$router.push({ name: nextRouteName });
          } else {
            this.errorMessage = this.$t('error.general');
          }
        } else {
          this.errorMessage = this.$t('error.general');
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.sign-in-mfa-setup-view {
  max-width: rem(400);
  padding-top: rem(60);
  position: relative;
  margin-right: auto;
  margin-left: auto;
}

.description {
  font-size: $font-size-base;
  line-height: rem(19);
  color: $auth-text-secondary;
  margin-bottom: rem(37);
  text-align: center;
}

.steps-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.step {
  position: relative;
  font-size: $font-size-md;
  padding-left: rem(18);
  margin-bottom: rem(27);

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    content: '';
    font-size: $font-size-md;
    font-weight: bold;
    color: $auth-text-muted;
  }

  &.step1::before {
    content: '1';
  }

  &.step2::before {
    content: '2';
  }

  &.step3::before {
    content: '3';
  }
}

.qrcode {
  border: solid rem(1) $auth-border;
  border-radius: $radius-md;
  margin: rem(-13) auto $space-5 auto;
}

.back-button {
  position: absolute;
  top: $space-5;
  left: rem(-50);
  font-size: $font-size-sm;
  text-transform: uppercase;
  display: flex;
  flex-direction: row;
  align-items: center;

  .v-icon {
    color: $auth-brand;
    margin-right: $space-2;
  }
}

h1 {
  text-align: center;
  margin-bottom: $space-4;
}

.error-message {
  color: $auth-error;
  font-size: $font-size-sm;
  line-height: $font-size-base;
  min-height: $font-size-base;
  margin-bottom: $space-2;
  text-align: center;
}
</style>
